import type BABYLON from 'babylonjs';

import type { Globals } from './globals';
import { compactOptions } from './options';

// cf. https://doc.babylonjs.com/typedoc/classes/babylon.arcrotatecamera
//     https://doc.babylonjs.com/divingDeeper/cameras/camera_introduction#arc-rotate-camera

export function isUserMesh(globals: Globals, mesh: BABYLON.AbstractMesh): boolean {
  return (
    !(mesh.material instanceof globals.MATERIALS.GridMaterial) &&
    !/^Background|^hdrSkyBox$/.test(mesh.id)
  );
}

export function focusMeshes(
  globals: Globals,
  scene: BABYLON.Scene,
  meshes?: BABYLON.AbstractMesh[]
): void {
  const camera = scene.activeCamera;
  if (camera instanceof globals.BABYLON.ArcRotateCamera) {
    // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/tools/sandbox/src/components/renderingZone.tsx#L187-L215
    const framingBehavior = camera.getBehaviorByName('Framing');
    if (framingBehavior instanceof globals.BABYLON.FramingBehavior) {
      const {
        radius,
        alpha,
        lowerAlphaLimit,
        upperAlphaLimit,
        beta,
        lowerBetaLimit,
        upperBetaLimit,
      } = camera;
      framingBehavior.framingTime = 0;
      framingBehavior.elevationReturnTime = -1;
      camera.lowerRadiusLimit = null;
      framingBehavior.zoomOnMeshesHierarchy(
        (meshes || scene.meshes.filter((mesh) => isUserMesh(globals, mesh))).filter(
          (mesh) => mesh.isVisible && mesh.isEnabled()
        )
      );
      camera.pinchPrecision = 200 / camera.radius;
      camera.upperRadiusLimit = 5 * camera.radius;
      camera.wheelDeltaPercentage = 0.01;
      camera.pinchDeltaPercentage = 0.01;

      // cf. https://github.com/BabylonJS/Babylon.js/blob/5.48.1/packages/dev/core/src/Helpers/sceneHelpers.ts#L144-L146
      camera.minZ = camera.radius * 0.01;
      camera.maxZ = camera.radius * 1000;
      camera.speed = camera.radius * 0.2;

      camera.radius *= radius;
      camera.lowerAlphaLimit = lowerAlphaLimit;
      camera.upperAlphaLimit = upperAlphaLimit;
      camera.lowerBetaLimit = lowerBetaLimit;
      camera.upperBetaLimit = upperBetaLimit;
      camera.alpha = alpha;
      camera.beta = beta;
    }
  }
}

export function attachCameraInput(
  camera: BABYLON.ArcRotateCamera,
  callbacks: {
    onButtonDown?: (
      this: BABYLON.ArcRotateCameraPointersInput,
      ...args: Parameters<BABYLON.ArcRotateCameraPointersInput['onButtonDown']>
    ) => void;
    onButtonUp?: (this: BABYLON.ArcRotateCameraPointersInput) => void;
    onLostFocus?: (this: BABYLON.ArcRotateCameraPointersInput) => void;
  } = {}
): void {
  const input = camera.inputs.attached.pointers as BABYLON.ArcRotateCameraPointersInput;
  const { onButtonDown, onButtonUp, onLostFocus } = input;
  Object.assign(input, {
    onButtonDown(evt): void {
      onButtonDown.call(this, evt);
      callbacks.onButtonDown?.call(this, evt);
    },
    onButtonUp(): void {
      onButtonUp.call(this);
      callbacks.onButtonUp?.call(this);
    },
    onLostFocus(): void {
      onLostFocus.call(this);
      callbacks.onLostFocus?.call(this);
    },
  } as BABYLON.ArcRotateCameraPointersInput);
}

export interface CameraOptions extends Partial<BABYLON.ArcRotateCamera> {}

export function createOrUpdateCamera(
  scene: BABYLON.Scene,
  options: CameraOptions = {}
): BABYLON.ArcRotateCamera {
  scene.createDefaultCamera(true, false, true);
  const camera = scene.activeCamera as BABYLON.ArcRotateCamera;
  Object.assign(camera, compactOptions(options));
  return camera;
}
